import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Space, Tabs, message, Spin, Result } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { PageHeader } from '@municipality/ui';
import { useMunicipalityAssignments } from '../hooks/useMunicipalityAssignments';
import { SuperAdminUserService } from '../services/users';
import MunicipalityAccessManager from '../components/MunicipalityAccessManager';
import MunicipalityAccessList from '../components/MunicipalityAccessList';
import MunicipalityAccessModal from '../components/MunicipalityAccessModal';
import {
  MunicipalityAccessLevel,
  MunicipalityPermissions,
  MunicipalityAssignment
} from '@municipality/types';
import { useAuth } from '../hooks/useAuth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@municipality/firebase';

const { TabPane } = Tabs;

const UserMunicipalityAccess: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useTranslation('users');
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { assignments, loading, error, refetch } = useMunicipalityAssignments(userId);
  const [submitting, setSubmitting] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<
    (MunicipalityAssignment & { municipalityName?: string }) | null
  >(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [targetUserData, setTargetUserData] = useState<{
    name?: string;
    displayName?: string;
    email?: string;
  } | null>(null);

  // Fetch target user data for notifications
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          const fullName = [data.name, data.surname].filter(Boolean).join(' ').trim() || data.displayName || data.email?.split('@')[0] || 'User';
          setTargetUserData({
            name: fullName,
            displayName: data.displayName,
            email: data.email,
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };

    fetchUserData();
  }, [userId]);

  const handleGrantAccess = async (
    municipalityIds: string[],
    accessLevel: MunicipalityAccessLevel,
    permissions: MunicipalityPermissions,
    options?: {
      expiresAt?: Date;
      notes?: string;
    }
  ) => {
    if (!userId || !currentUser?.uid) {
      message.error(t('notAuthenticated', 'Not authenticated'));
      return;
    }

    setSubmitting(true);
    try {
      await SuperAdminUserService.assignMunicipalities(
        userId,
        municipalityIds,
        accessLevel,
        permissions,
        currentUser.uid,
        {
          ...options,
          grantedByName: currentUser.displayName || currentUser.email || 'Unknown',
          userName: targetUserData?.name || targetUserData?.displayName || 'User',
          userEmail: targetUserData?.email,
        }
      );

      message.success(
        t(
          'accessGrantedSuccess',
          `Access granted to ${municipalityIds.length} ${municipalityIds.length === 1 ? 'municipality' : 'municipalities'}`
        )
      );
      
      refetch();
    } catch (error) {
      console.error('Error granting access:', error);
      message.error(t('accessGrantedError', 'Failed to grant access'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (municipalityId: string, municipalityName?: string) => {
    if (!userId) return;

    try {
      await SuperAdminUserService.revokeMunicipalityAccess(userId, municipalityId, {
        revokedBy: currentUser?.displayName || currentUser?.email || 'Unknown',
        userName: targetUserData?.name || targetUserData?.displayName || 'User',
        userEmail: targetUserData?.email,
        municipalityName,
      });
      message.success(t('accessRevoked', 'Access revoked successfully'));
      refetch();
    } catch (error) {
      console.error('Error revoking access:', error);
      message.error(t('revokeError', 'Failed to revoke access'));
    }
  };

  const handleEdit = (assignment: MunicipalityAssignment & { municipalityName?: string }) => {
    setEditingAssignment(assignment);
    setModalVisible(true);
  };

  const handleSave = async (
    municipalityId: string,
    updates: {
      accessLevel?: MunicipalityAccessLevel;
      permissions?: MunicipalityPermissions;
      expiresAt?: Date | null;
      notes?: string;
    }
  ) => {
    if (!userId) return;

    try {
      await SuperAdminUserService.updateMunicipalityPermissions(userId, municipalityId, updates);
      message.success(t('assignmentUpdated', 'Assignment updated successfully'));
      setModalVisible(false);
      setEditingAssignment(null);
      refetch();
    } catch (error) {
      console.error('Error updating assignment:', error);
      message.error(t('updateError', 'Failed to update assignment'));
      throw error;
    }
  };

  const handleResend = async (municipalityId: string, municipalityName?: string) => {
    if (!userId) return;

    try {
      await SuperAdminUserService.resendExpiredAssignment(userId, municipalityId, undefined, {
        renewedBy: currentUser?.displayName || currentUser?.email || 'Unknown',
        userName: targetUserData?.name || targetUserData?.displayName || 'User',
        userEmail: targetUserData?.email,
        municipalityName,
      });
      message.success(t('assignmentRenewed', 'Assignment renewed successfully'));
      refetch();
    } catch (error) {
      console.error('Error renewing assignment:', error);
      message.error(t('renewError', 'Failed to renew assignment'));
    }
  };

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <PageHeader
          title={t('municipalityAccess', 'Municipality access')}
          subtitle={t('manageUserMunicipalityAssignments', 'Manage user municipality assignments')}
          extra={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
              {t('backToUsers', 'Back to users')}
            </Button>
          }
        />
        <Result
          status="error"
          title={t('errorLoadingAssignments', 'Error loading assignments')}
          subTitle={error}
          extra={
            <Button type="primary" onClick={refetch}>
              {t('retry', 'Retry')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title={t('municipalityAccess', 'Municipality access')}
        subtitle={t('manageUserMunicipalityAssignments', 'Manage user municipality assignments')}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading}>
              {t('refresh', 'Refresh')}
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
              {t('backToUsers', 'Back to users')}
            </Button>
          </Space>
        }
      />

      <Card>
        {loading && !assignments.length ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Tabs defaultActiveKey="list">
            <TabPane tab={t('currentAssignments', 'Current assignments')} key="list">
              <MunicipalityAccessList
                assignments={assignments}
                loading={loading}
                onEdit={handleEdit}
                onRevoke={handleRevoke}
                onResend={handleResend}
              />
            </TabPane>

            <TabPane tab={t('grantNewAccess', 'Grant new access')} key="grant">
              <MunicipalityAccessManager
                onGrantAccess={handleGrantAccess}
                loading={submitting}
              />
            </TabPane>
          </Tabs>
        )}
      </Card>

      <MunicipalityAccessModal
        visible={modalVisible}
        assignment={editingAssignment}
        onCancel={() => {
          setModalVisible(false);
          setEditingAssignment(null);
        }}
        onSave={handleSave}
        loading={submitting}
      />
    </div>
  );
};

export default UserMunicipalityAccess;
